'use client';

import React from 'react';
import { useParams } from 'next/navigation';
import BigNumber from 'bignumber.js';
import { FiCopy, FiCheck } from 'react-icons/fi';
import styles from './TransactionDetailPage.module.css';
import useApiQuery from 'lib/api/useApiQuery';
import { formatTimestamp } from '../../utils/formatTimestamp';

const TransactionDetailPage: React.FC = () => {
  const params = useParams();
  const hash = params?.hash as string;
  const [copiedField, setCopiedField] = React.useState<string | null>(null);

  const txQuery = useApiQuery('tx', {
    pathParams: { hash },
    queryOptions: {
      enabled: Boolean(hash),
    },
  });

  const tx = txQuery.data as any;

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatWei = (value: string | null | undefined) => {
    if (!value || value === '0') return '0 OGPU';
    const bn = BigNumber(value);
    const eth = bn.dividedBy(BigNumber(10).pow(18));
    return `${eth.toFixed(18).replace(/\.?0+$/, '')} OGPU`;
  };

  const formatGwei = (value: string | null | undefined) => {
    if (!value || value === '0') return '0 Gwei';
    const bn = BigNumber(value);
    const gwei = bn.dividedBy(BigNumber(10).pow(9));
    return `${gwei.toFixed(2)} Gwei`;
  };

  const isLoading = txQuery.isLoading;

  if (!isLoading && !tx) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Transaction not found</div>
      </div>
    );
  }

  const gasUsedPercent = tx?.gas_used && tx?.gas_limit
    ? (Number(tx.gas_used) / Number(tx.gas_limit) * 100).toFixed(1)
    : '0';

  const skeleton = (width = '120px') => (
    <span className={styles.skeleton} style={{ width }} />
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Transaction Details</h1>
        {!isLoading && tx?.status === 'ok' ? (
          <span className={`${styles.statusBadge} ${styles.statusSuccess}`}>Success</span>
        ) : !isLoading && tx?.status === 'error' ? (
          <span className={`${styles.statusBadge} ${styles.statusFailed}`}>Failed</span>
        ) : null}
      </div>

      <div className={styles.detailCard}>
        <div className={styles.row}>
          <div className={styles.label}>Transaction Hash</div>
          <div className={styles.value}>
            {isLoading ? skeleton('100%') : (
              <>
                <span className={styles.hashFull}>{tx.hash}</span>
                <button
                  className={styles.copyButton}
                  onClick={() => copyToClipboard(tx.hash, 'hash')}
                >
                  {copiedField === 'hash' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                </button>
              </>
            )}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Status</div>
          <div className={styles.value}>
            {isLoading ? skeleton('80px') : tx?.status === 'ok' ? (
              <span className={`${styles.statusBadge} ${styles.statusSuccess}`}>Success</span>
            ) : (
              <span className={`${styles.statusBadge} ${styles.statusFailed}`}>Failed</span>
            )}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Block</div>
          <div className={styles.value}>
            {isLoading ? skeleton('100px') : (
              <>
                <a href={`/block/${tx.block_number}`} className={styles.link}>
                  {tx.block_number}
                </a>
                {tx.confirmations && (
                  <span style={{ marginLeft: 8, color: 'rgba(255,255,255,0.4)', fontSize: '0.8125rem' }}>
                    {tx.confirmations} Block Confirmations
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Timestamp</div>
          <div className={styles.value}>
            {isLoading ? skeleton('200px') : tx?.timestamp ? formatTimestamp(tx.timestamp) : '—'}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>From</div>
          <div className={styles.value}>
            {isLoading ? skeleton('100%') : (
              <>
                <a href={`/address/${tx.from?.hash}`} className={styles.link}>
                  {tx.from?.hash}
                </a>
                <button
                  className={styles.copyButton}
                  onClick={() => copyToClipboard(tx.from?.hash || '', 'from')}
                >
                  {copiedField === 'from' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                </button>
              </>
            )}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>To</div>
          <div className={styles.value}>
            {isLoading ? skeleton('100%') : tx?.to ? (
              <>
                <a href={`/address/${tx.to.hash}`} className={styles.link}>
                  {tx.to.hash}
                </a>
                {tx.to.is_contract && <span className={styles.badge} style={{ marginLeft: 6 }}>Contract</span>}
                <button
                  className={styles.copyButton}
                  onClick={() => copyToClipboard(tx.to?.hash || '', 'to')}
                >
                  {copiedField === 'to' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                </button>
              </>
            ) : tx?.created_contract ? (
              <>
                <a href={`/address/${tx.created_contract.hash}`} className={styles.link}>
                  {tx.created_contract.hash}
                </a>
                <span className={styles.badge} style={{ marginLeft: 6 }}>Created</span>
              </>
            ) : '—'}
          </div>
        </div>

        {!isLoading && tx?.transaction_types && (
          <div className={styles.row}>
            <div className={styles.label}>Transaction Type</div>
            <div className={styles.value}>
              {tx.transaction_types.map((t: string) => (
                <span key={t} className={styles.badge} style={{ marginRight: 6 }}>
                  {t.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className={styles.row}>
          <div className={styles.label}>Value</div>
          <div className={styles.value}>{isLoading ? skeleton('180px') : formatWei(tx.value)}</div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Transaction Fee</div>
          <div className={styles.value}>{isLoading ? skeleton('180px') : formatWei(tx.fee?.value)}</div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Gas Price</div>
          <div className={styles.value}>{isLoading ? skeleton('120px') : formatGwei(tx.gas_price)}</div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Gas Limit & Usage</div>
          <div className={styles.value}>
            {isLoading ? skeleton('200px') : (
              <div className={styles.gasBar}>
                <span>{Number(tx.gas_limit).toLocaleString()}</span>
                <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
                <span>{Number(tx.gas_used).toLocaleString()} ({gasUsedPercent}%)</span>
                <div className={styles.gasBarTrack}>
                  <div className={styles.gasBarFill} style={{ width: `${gasUsedPercent}%` }} />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Nonce</div>
          <div className={styles.value}>{isLoading ? skeleton('60px') : tx.nonce}</div>
        </div>

        <div className={styles.row}>
          <div className={styles.label}>Position</div>
          <div className={styles.value}>{isLoading ? skeleton('60px') : tx.position}</div>
        </div>

        {!isLoading && tx?.base_fee_per_gas && (
          <div className={styles.row}>
            <div className={styles.label}>Base Fee Per Gas</div>
            <div className={styles.value}>{formatGwei(tx.base_fee_per_gas)}</div>
          </div>
        )}

        {!isLoading && tx?.raw_input && tx.raw_input !== '0x' && (
          <div className={styles.row}>
            <div className={styles.label}>Raw Input</div>
            <div className={styles.value}>
              <div className={styles.rawInput}>{tx.raw_input}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransactionDetailPage;
